import { DOCUMENT } from '@angular/common';
import { APP_INITIALIZER, ApplicationConfig, inject, provideZoneChangeDetection } from '@angular/core';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { routes } from './app.routes';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { socialAuthConfig } from '@modules/auth/social-login-config';
import { environment } from '../../environments/environment';
import { HttpRequestInterceptor } from './core/interceptors/http-request.interceptor';
import { LoadingInterceptor } from './core/interceptors/loading.interceptor';
import { TokenRefreshInterceptor } from './core/interceptors/token-refresh.interceptor';
import { AnalyticsService } from './core/services/analytics.service';
import { SelectivePreloadingStrategy } from './core/services/selective-preloading-strategy';

/**
 * Interface to extend the Window object with Google Analytics properties and methods.
 */
interface GTagWindow extends Window {
  dataLayer: unknown[];
  gtag: (...args: unknown[]) => void;
}

/**
 * Initializes Google Analytics by dynamically loading the gtag.js script and configuring it with the provided GA ID.
 */
function initGoogleAnalytics(doc: Document): void {
  if (!environment.gaId) return;

  const win = window as unknown as GTagWindow;
  win.dataLayer = win.dataLayer ?? [];
  win.gtag = function gtag(): void {
    // gtag.js only processes native Arguments objects; pushing a plain array (e.g. rest params) is silently ignored.
    // biome-ignore lint/style/noArguments: gtag.js requires the native `arguments` object, not an array.
    win.dataLayer.push(arguments);
  };
  win.gtag('js', new Date());
  win.gtag('config', environment.gaId);

  const script = doc.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.gaId}`;
  doc.head.appendChild(script);
}

// turn on scroll to top on navigation
const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature = withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(routes, inMemoryScrollingFeature, withPreloading(SelectivePreloadingStrategy)),
    provideAnimationsAsync(),
    {
      provide: 'SocialAuthServiceConfig',
      useValue: socialAuthConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenRefreshInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    // Load Google Analytics 4 at startup (no-op when environment.gaMeasurementId is empty)
    {
      provide: APP_INITIALIZER,
      useFactory: (analytics: AnalyticsService) => () => analytics.init(),
      deps: [AnalyticsService],
      multi: true,
    },
    // Load Google Analytics at startup (no-op when environment.gaId is empty)
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const doc = inject(DOCUMENT);
        return () => initGoogleAnalytics(doc);
      },
      multi: true,
    },
  ],
};
